// Where a storybook page's photos go on paper: the same arrangement as the book on screen (styles/storybook.css), so
// the printed book is the book people paged through. Each photo has an equal share of the page (its plate) and is as
// large as fits in it, whole and keeping its shape, with room for its words beneath. One is centered; two sit one over
// the other; three are two above and the third the same size centered below; four are two by two. Photos gather
// toward each other across the gap between rows, so a page reads as one group. Measures are in any one unit.

export type Box = { x: number; y: number; width: number; height: number }

export function plates(count: number, body: Box, gap: { row: number; column: number }): Box[] {
  const rows = count === 1 ? 1 : 2
  const columns = count >= 3 ? 2 : 1
  const width = (body.width - (columns - 1) * gap.column) / columns
  const height = (body.height - (rows - 1) * gap.row) / rows
  return Array.from({ length: count }, (_, i) => {
    const row = Math.floor(i / columns)
    const column = i % columns
    // The third of three sits alone in its row, centered.
    const x = count === 3 && i === 2 ? body.x + (body.width - width) / 2 : body.x + column * (width + gap.column)
    return { x, y: body.y + row * (height + gap.row), width, height }
  })
}

// The photo in its plate: as large as fits above its words (`words`, the room they take below it, gap included),
// centered across, and in a two-row page pushed toward the other row.
export function placed(plate: Box, ratio: number, words: number, row: 'top' | 'bottom' | 'only'): Box {
  const width = Math.min(plate.width, Math.max(0, plate.height - words) * ratio)
  const height = width / ratio
  const group = height + words
  const y =
    row === 'top' ? plate.y + plate.height - group : row === 'bottom' ? plate.y : plate.y + (plate.height - group) / 2
  return { x: plate.x + (plate.width - width) / 2, y, width, height }
}

export const rowOf = (count: number, index: number): 'top' | 'bottom' | 'only' =>
  count === 1 ? 'only' : index < (count >= 3 ? 2 : 1) ? 'top' : 'bottom'

// Where filled photos go (edge to edge, or thin margins): one fills the page; two share it, one over the other on an
// upright page and side by side on a wide one; three are two above and one the full width below; four are two by two.
export function fillPlates(count: number, body: Box, gap: number, upright: boolean): Box[] {
  const rows = count === 1 || (count === 2 && !upright) ? 1 : 2
  const height = (body.height - (rows - 1) * gap) / rows
  return Array.from({ length: count }, (_, i) => {
    const row = rows === 1 || count === 2 ? (rows === 1 ? 0 : i) : i < 2 ? 0 : 1
    const inRow = count === 1 ? 1 : count === 2 ? (rows === 1 ? 2 : 1) : row === 0 ? 2 : count - 2
    const column = count === 2 ? (rows === 1 ? i : 0) : row === 0 ? i : i - 2
    const width = (body.width - (inRow - 1) * gap) / inRow
    return { x: body.x + column * (width + gap), y: body.y + row * (height + gap), width, height }
  })
}

// The room a chapter's year takes over its first page, in inches.
export const YEAR_ROOM = 0.45

// A page of photos in a printed book's look, in inches from the trimmed page's top left: each photo's share of the
// page. Kept whole, a photo is then placed in its plate with `placed`; filled, it's cropped to the plate. Edge to edge
// reaches `bleed` past the cut. The PDF and the builder's preview both lay pages out with this, so they match.
export function photoPlates(o: {
  count: number
  trim: { width: number; height: number }
  margins: { top: number; side: number; bottom: number; gap: number }
  fill: boolean
  bleed: number
  opens: boolean
  gutter: number
  dx: number
}): Box[] {
  const { trim: t, margins: m } = o
  if (o.fill) {
    const body =
      o.bleed > 0
        ? { x: -o.bleed, y: -o.bleed, width: t.width + 2 * o.bleed, height: t.height + 2 * o.bleed }
        : { x: m.side + o.dx, y: m.top, width: t.width - 2 * m.side - o.gutter, height: t.height - m.top - m.bottom }
    return fillPlates(o.count, body, m.gap, t.width <= t.height)
  }
  const top = m.top + (o.opens ? YEAR_ROOM : 0)
  const body = { x: m.side + o.dx, y: top, width: t.width - 2 * m.side - o.gutter, height: t.height - top - m.bottom }
  return plates(o.count, body, { row: m.gap, column: m.gap })
}
